import { ApiError } from '../errors/api-error.js';
import { withRetry } from '../utils/retry.js';

export class IntegrationConnectionService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    async getAll(params = {}) {
        try {
            const response = await withRetry(() => this.api.getAll(params), { maxRetries: 2 });
            return {
                success: true,
                data: response.data || [],
                pagination: response.pagination || {},
                uniqueValues: response.uniqueValues,
                message: response.message || 'Connections loaded',
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch connections');
        }
    }

    async getById(id) {
        try {
            const response = await withRetry(() => this.api.getById(id), { maxRetries: 2 });
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch connection');
        }
    }

    async create(data) {
        try {
            const response = await this.api.create(data);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to create connection');
        }
    }

    async update(id, data) {
        try {
            const response = await this.api.update(id, data);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to update connection');
        }
    }

    async delete(id) {
        try {
            const response = await this.api.delete(id);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete connection');
        }
    }

    async bulkDelete(ids) {
        try {
            const response = await this.api.batchDelete(ids);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to bulk delete connections');
        }
    }

    async listProviders() {
        try {
            const response = await withRetry(() => this.api.listProviders(), { maxRetries: 2 });
            return { success: true, data: response.data || [] };
        } catch (error) {
            throw this._handleError(error, 'Failed to list providers');
        }
    }

    async test(id) {
        try {
            const response = await this.api.test(id);
            return { success: response.success !== false, data: response.data, message: response.message, error: response.error };
        } catch (error) {
            throw this._handleError(error, 'Connection test failed');
        }
    }

    async listEndpoints(id) {
        try {
            const response = await withRetry(() => this.api.listEndpoints(id), { maxRetries: 2 });
            return { success: true, data: response.data || [] };
        } catch (error) {
            throw this._handleError(error, 'Failed to list endpoints');
        }
    }

    async createEndpoint(id, data) {
        try {
            const response = await this.api.createEndpoint(id, data);
            return { success: true, data: response.data };
        } catch (error) {
            throw this._handleError(error, 'Failed to create endpoint');
        }
    }

    async updateEndpoint(id, endpointId, data) {
        try {
            const response = await this.api.updateEndpoint(id, endpointId, data);
            return { success: true, data: response.data };
        } catch (error) {
            throw this._handleError(error, 'Failed to update endpoint');
        }
    }

    async deleteEndpoint(id, endpointId) {
        try {
            const response = await this.api.deleteEndpoint(id, endpointId);
            return { success: true, data: response.data };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete endpoint');
        }
    }

    async invokeEndpoint(id, endpointKey, body = {}, query = {}) {
        try {
            const response = await this.api.invokeEndpoint(id, endpointKey, body, query);
            return {
                success: response.success !== false,
                data: response.data,
                meta: response.meta,
                error: response.error,
            };
        } catch (error) {
            throw this._handleError(error, 'Endpoint invoke failed');
        }
    }

    async listInvocations(id, params = {}) {
        try {
            const response = await withRetry(() => this.api.listInvocations(id, params), { maxRetries: 2 });
            return { success: true, data: response.data || [], pagination: response.pagination };
        } catch (error) {
            throw this._handleError(error, 'Failed to list invocations');
        }
    }

    async listMarketplace(params = {}) {
        try {
            const response = await withRetry(() => this.api.listMarketplace(params), { maxRetries: 2 });
            return { success: true, data: response.data || [] };
        } catch (error) {
            throw this._handleError(error, 'Failed to list marketplace');
        }
    }

    async listProviderDefinitions(params = {}) {
        try {
            const response = await withRetry(() => this.api.listProviderDefinitions(params), { maxRetries: 2 });
            return {
                success: true,
                data: response.data || [],
                meta: response.pagination || response.meta || {},
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to list provider definitions');
        }
    }

    async createProviderDefinition(data) {
        try {
            const response = await this.api.createProviderDefinition(data);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to create provider definition');
        }
    }

    async updateProviderDefinition(id, data) {
        try {
            const response = await this.api.updateProviderDefinition(id, data);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to update provider definition');
        }
    }

    async deleteProviderDefinition(id) {
        try {
            const response = await this.api.deleteProviderDefinition(id);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete provider definition');
        }
    }

    async listResources(id) {
        try {
            const response = await withRetry(() => this.api.listResources(id), { maxRetries: 2 });
            return { success: true, data: response.data || [] };
        } catch (error) {
            throw this._handleError(error, 'Failed to list resources');
        }
    }

    async discoverResource(id, resourceKey, body = {}) {
        try {
            const response = await this.api.discoverResource(id, resourceKey, body);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to discover resource');
        }
    }

    async listTargetFields(id, entityType) {
        try {
            const response = await withRetry(
                () => this.api.listTargetFields(id, entityType),
                { maxRetries: 2 },
            );
            return { success: true, data: response.data || [] };
        } catch (error) {
            throw this._handleError(error, 'Failed to list target fields');
        }
    }

    async listMappings(id, params = {}) {
        try {
            const response = await withRetry(() => this.api.listMappings(id, params), { maxRetries: 2 });
            return { success: true, data: response.data || [], pagination: response.pagination };
        } catch (error) {
            throw this._handleError(error, 'Failed to list mappings');
        }
    }

    async createMapping(id, data) {
        try {
            const response = await this.api.createMapping(id, data);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to create mapping');
        }
    }

    async getMapping(id, mappingId) {
        try {
            const response = await withRetry(() => this.api.getMapping(id, mappingId), { maxRetries: 2 });
            return { success: true, data: response.data };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch mapping');
        }
    }

    async updateMapping(id, mappingId, data) {
        try {
            const response = await this.api.updateMapping(id, mappingId, data);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to update mapping');
        }
    }

    async deleteMapping(id, mappingId) {
        try {
            const response = await this.api.deleteMapping(id, mappingId);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete mapping');
        }
    }

    async cloneMapping(id, mappingId, data = {}) {
        try {
            const response = await this.api.cloneMapping(id, mappingId, data);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to clone mapping');
        }
    }

    async publishMapping(id, mappingId, data = {}) {
        try {
            const response = await this.api.publishMapping(id, mappingId, data);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to publish mapping');
        }
    }

    async previewMapping(id, mappingId, data = {}) {
        try {
            const response = await this.api.previewMapping(id, mappingId, data);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to preview mapping');
        }
    }

    async importMapping(id, mappingId, data) {
        try {
            const response = await this.api.importMapping(id, mappingId, data);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to import mapping');
        }
    }

    async listRuns(id, params = {}) {
        try {
            const response = await withRetry(() => this.api.listRuns(id, params), { maxRetries: 2 });
            return { success: true, data: response.data || [], pagination: response.pagination };
        } catch (error) {
            throw this._handleError(error, 'Failed to list runs');
        }
    }

    async getRun(id, runId) {
        try {
            const response = await withRetry(() => this.api.getRun(id, runId), { maxRetries: 2 });
            return { success: true, data: response.data };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch run');
        }
    }

    async listRunItems(id, runId, params = {}) {
        try {
            const response = await withRetry(
                () => this.api.listRunItems(id, runId, params),
                { maxRetries: 2 },
            );
            return { success: true, data: response.data || [], pagination: response.pagination };
        } catch (error) {
            throw this._handleError(error, 'Failed to list run items');
        }
    }

    async replayRunFailures(id, runId, data = {}) {
        try {
            const response = await this.api.replayRunFailures(id, runId, data);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to replay run failures');
        }
    }

    async listSchedules(id) {
        try {
            const response = await withRetry(() => this.api.listSchedules(id), { maxRetries: 2 });
            return { success: true, data: response.data || [] };
        } catch (error) {
            throw this._handleError(error, 'Failed to list schedules');
        }
    }

    async upsertSchedule(id, data) {
        try {
            const response = await this.api.upsertSchedule(id, data);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to save schedule');
        }
    }

    async runScheduleNow(id, syncStateId) {
        try {
            const response = await this.api.runScheduleNow(id, syncStateId);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to run schedule');
        }
    }

    async pauseSchedule(id, syncStateId, data = {}) {
        try {
            const response = await this.api.pauseSchedule(id, syncStateId, data);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to pause schedule');
        }
    }

    async resetCheckpoint(id, syncStateId, data = {}) {
        try {
            const response = await this.api.resetCheckpoint(id, syncStateId, data);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to reset checkpoint');
        }
    }

    async executeAction(id, data) {
        try {
            const response = await this.api.executeAction(id, data);
            return {
                success: response.success !== false,
                data: response.data,
                message: response.message,
                error: response.error,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to execute action');
        }
    }

    async resolveConflict(id, itemId, data) {
        try {
            const response = await this.api.resolveConflict(id, itemId, data);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to resolve conflict');
        }
    }

    async upgradeProvider(id, data) {
        try {
            const response = await this.api.upgradeProvider(id, data);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to upgrade provider');
        }
    }

    async getWebhookStatus(id) {
        try {
            const response = await this.api.getWebhookStatus(id);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to load webhook status');
        }
    }

    async enableWebhook(id) {
        try {
            const response = await this.api.enableWebhook(id);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to enable webhook');
        }
    }

    async rotateWebhookSecret(id) {
        try {
            const response = await this.api.rotateWebhookSecret(id);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to rotate webhook secret');
        }
    }

    async disableWebhook(id) {
        try {
            const response = await this.api.disableWebhook(id);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to disable webhook');
        }
    }

    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) throw error;
        if (error.statusCode || error.status) throw ApiError.fromResponse(error);
        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
